import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';

import type { IdMeta } from '#components/mobile/travesia/catalogo';
import { leerTravesia } from '#components/mobile/travesia/estado';

import { metaDeObjetivo, proximoVencimiento } from './objetivos';
import type { Objetivo } from './objetivos';

/**
 * Travesías (metas con fecha: objetivos `una-vez` / `anual`) y sus recuerdos:
 * «La botella y la bitácora».
 *
 * Funciones puras, sin React. Almacén: prefs sincronizadas `recuerdo:${string}`
 * (cifradas y compartidas con el presupuesto), partidas por quién escribe para
 * que la «última escritura gana» de la sincronización no pierda nada:
 *
 * - `recuerdo:<rid>`            base: carta y copias. Solo gestos explícitos
 *                               (y la adopción, que es determinista).
 * - `recuerdo:<rid>:d:<disp>`   aporte de un dispositivo (firma, llegada,
 *                               apertura, respuesta). Solo ese dispositivo.
 * - `recuerdo:<rid>:foto`       data URL JPEG ≤ TOPE_FOTO (fase 2).
 *
 * `rid = <idCategoría>@<ciclo>` (ciclo = vencimiento `AAAA-MM-DD`): es
 * determinista, así que dos móviles no crean dos recuerdos de la misma
 * travesía. Borrar es escribir `''` (lápida). Nada retira una llegada
 * (gastar lo ahorrado era el objetivo).
 */

export const PREFIJO_RECUERDO = 'recuerdo:';
const MARCA_APORTE = ':d:';
const SUFIJO_FOTO = ':foto';

/** Polaroid 4:5 y tope del data URL (medidos en R2-F §2). Fase 2. */
export const FOTO = { ancho: 240, alto: 300, calidad: 0.8 } as const;
export const TOPE_FOTO = 40_000;
export const ESCALERA_FOTO = [
  { ancho: 240, alto: 300, calidad: 0.8 },
  { ancho: 240, alto: 300, calidad: 0.7 },
  { ancho: 240, alto: 300, calidad: 0.6 },
  { ancho: 200, alto: 250, calidad: 0.7 },
] as const;
export const AVISO_PESO = 2 * 1024 * 1024;
export const LIMITE_PESO = 3 * 1024 * 1024;

/** «Una nota para el día que llegues». */
export const LARGO_CARTA = 280;
/** Línea «si-entonces» («Cuando cobre, aparto 100 €»). */
export const LARGO_SI_ENTONCES = 120;
/** Firma («Firmar también») y respuesta de llegada. */
export const LARGO_FIRMA = 140;
export const LARGO_RESPUESTA = 280;

export type ClaveRecuerdo = `recuerdo:${string}`;
export type ObjetivoConFecha = Extract<Objetivo, { tipo: 'anual' | 'una-vez' }>;
export type EstadoTravesia = 'navegando' | 'archivada' | 'devuelta';

const ESTADOS: readonly EstadoTravesia[] = [
  'navegando',
  'archivada',
  'devuelta',
];
const CLASES: readonly IdMeta[] = [
  'colchon',
  'viaje',
  'compra',
  'carne',
  'otra',
];

export type RecuerdoBase = {
  v: 1;
  rid: string;
  categoriaId: string;
  tipo: ObjetivoConFecha['tipo'];
  /** Vencimiento del ciclo `AAAA-MM-DD` (la «fecha» de la travesía). */
  ciclo: string;
  /** Tipo de meta (los cinco de `DibujosMetas`): vidrio y sello. */
  clase: IdMeta;
  /** Copias al zarpar: sobreviven a borrar o renombrar la categoría. */
  nombre: string;
  emoji: string | null;
  metaAlZarpar: IntegerAmount;
  saldoAlZarpar: IntegerAmount;
  /** `sum-amount` del mes de zarpe al zarpar (≤ 0): el gasto previo no arma. */
  gastadoAlZarpar: IntegerAmount;
  /** `AAAA-MM-DD`. */
  zarpoEn: string;
  /** ≤ LARGO_CARTA; '' si no se escribió. */
  carta: string;
  /** Línea «si-entonces» opcional; '' si no hay. */
  siEntonces: string;
  autor: string | null;
  /** «Guardar cerrada hasta…» (`AAAA-MM-DD`) o null. */
  abrirDesde: string | null;
  estado: EstadoTravesia;
  /** «Cambiar de rumbo»: rid de la travesía que heredó la carta. */
  rumboA: string | null;
  /** Día en que se cerró sin cumplir (archivar, devolver o cambiar de rumbo). */
  cerradaEn: string | null;
};

export type AporteDispositivo = {
  v: 1;
  quien: string | null;
  /** «Firmar también»: la línea de quien firma desde este dispositivo. */
  firma: string | null;
  llegadaEn: string | null;
  saldoAlLlegar: IntegerAmount | null;
  metaAlLlegar: IntegerAmount | null;
  /** Meses con dinero asignado desde el zarpe (marcas del cuello). */
  aportacionesAlLlegar: number | null;
  abiertaEn: string | null;
  /** «Responder a quien zarpó». */
  respuesta: string | null;
};

export type Llegada = {
  fecha: string;
  saldo: IntegerAmount;
  meta: IntegerAmount;
  aportaciones: number | null;
  /** Meta bajada antes de llegar: se anota en la cartela. */
  ajustada: boolean;
  /** Días respecto al ciclo (negativo = antes del plazo). */
  diasRespectoPlazo: number;
};

export type LineaFirmada = {
  disp: string;
  quien: string | null;
  texto: string;
};

export type Recuerdo = {
  base: RecuerdoBase;
  aportes: Readonly<Record<string, AporteDispositivo>>;
  tieneFoto: boolean;
  llegada: Llegada | null;
  abierta: { fecha: string; quien: string | null } | null;
  firmas: LineaFirmada[];
  respuestas: LineaFirmada[];
};

// ---------------------------------------------------------------------------
// Claves
// ---------------------------------------------------------------------------

export function ridDe(categoriaId: string, ciclo: string): string {
  return `${categoriaId}@${ciclo}`;
}
export function claveBase(rid: string): ClaveRecuerdo {
  return `${PREFIJO_RECUERDO}${rid}`;
}
export function claveAporte(rid: string, disp: string): ClaveRecuerdo {
  return `${PREFIJO_RECUERDO}${rid}${MARCA_APORTE}${disp}`;
}
export function claveFoto(rid: string): ClaveRecuerdo {
  return `${PREFIJO_RECUERDO}${rid}${SUFIJO_FOTO}`;
}

export type ClaveLeida =
  | { parte: 'base'; rid: string }
  | { parte: 'aporte'; rid: string; disp: string }
  | { parte: 'foto'; rid: string };

function ridValido(rid: string): boolean {
  const i = rid.lastIndexOf('@');
  return i > 0 && FECHA.test(rid.slice(i + 1)) && !rid.includes(':');
}

/** Interpreta una clave de pref; null si no es de recuerdos. */
export function leerClave(clave: string): ClaveLeida | null {
  if (!clave.startsWith(PREFIJO_RECUERDO)) {
    return null;
  }
  const resto = clave.slice(PREFIJO_RECUERDO.length);
  const i = resto.indexOf(MARCA_APORTE);
  if (i > 0) {
    const rid = resto.slice(0, i);
    const disp = resto.slice(i + MARCA_APORTE.length);
    return disp && !disp.includes(':') && ridValido(rid)
      ? { parte: 'aporte', rid, disp }
      : null;
  }
  if (resto.endsWith(SUFIJO_FOTO)) {
    const rid = resto.slice(0, -SUFIJO_FOTO.length);
    return ridValido(rid) ? { parte: 'foto', rid } : null;
  }
  return ridValido(resto) ? { parte: 'base', rid: resto } : null;
}

// ---------------------------------------------------------------------------
// Lectores tolerantes (lo que no se entiende se ignora, como `leerTravesia`)
// ---------------------------------------------------------------------------

const FECHA = /^\d{4}-\d{2}-\d{2}$/;

function objeto(
  raw: string | null | undefined,
): Record<string, unknown> | null {
  if (!raw) {
    return null;
  }
  try {
    const v: unknown = JSON.parse(raw);
    return v != null && typeof v === 'object' && !Array.isArray(v)
      ? (v as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function texto(v: unknown, largo: number): string {
  return typeof v === 'string' ? v.slice(0, largo) : '';
}
function textoONull(v: unknown, largo = Infinity): string | null {
  return typeof v === 'string' && v !== '' ? v.slice(0, largo) : null;
}
function fechaONull(v: unknown): string | null {
  return typeof v === 'string' && FECHA.test(v) ? v : null;
}
function enteroONull(v: unknown): number | null {
  return typeof v === 'number' && Number.isInteger(v) ? v : null;
}

/** Base de un recuerdo; null si el JSON está roto, es de otra versión o le falta algo. */
export function parseBase(raw: string | null | undefined): RecuerdoBase | null {
  const v = objeto(raw);
  if (
    !v ||
    v.v !== 1 ||
    typeof v.rid !== 'string' ||
    !ridValido(v.rid) ||
    typeof v.categoriaId !== 'string' ||
    !v.categoriaId ||
    (v.tipo !== 'una-vez' && v.tipo !== 'anual') ||
    !fechaONull(v.ciclo) ||
    !fechaONull(v.zarpoEn) ||
    !ESTADOS.includes(v.estado as EstadoTravesia)
  ) {
    return null;
  }
  const meta = enteroONull(v.metaAlZarpar);
  const saldo = enteroONull(v.saldoAlZarpar);
  if (meta == null || saldo == null) {
    return null;
  }
  return {
    v: 1,
    rid: v.rid,
    categoriaId: v.categoriaId,
    tipo: v.tipo,
    ciclo: v.ciclo as string,
    clase: CLASES.includes(v.clase as IdMeta) ? (v.clase as IdMeta) : 'otra',
    nombre: texto(v.nombre, 200),
    emoji: textoONull(v.emoji, 16),
    metaAlZarpar: meta,
    saldoAlZarpar: saldo,
    gastadoAlZarpar: Math.min(0, enteroONull(v.gastadoAlZarpar) ?? 0),
    zarpoEn: v.zarpoEn as string,
    carta: texto(v.carta, LARGO_CARTA),
    siEntonces: texto(v.siEntonces, LARGO_SI_ENTONCES),
    autor: textoONull(v.autor, 200),
    abrirDesde: fechaONull(v.abrirDesde),
    estado: v.estado as EstadoTravesia,
    rumboA:
      typeof v.rumboA === 'string' && ridValido(v.rumboA) ? v.rumboA : null,
    cerradaEn: fechaONull(v.cerradaEn),
  };
}

/** Aporte de un dispositivo; los campos ausentes o raros valen null. */
export function parseAporte(
  raw: string | null | undefined,
): AporteDispositivo | null {
  const v = objeto(raw);
  if (!v || v.v !== 1) {
    return null;
  }
  return {
    v: 1,
    quien: textoONull(v.quien, 200),
    firma: textoONull(v.firma, LARGO_FIRMA),
    llegadaEn: fechaONull(v.llegadaEn),
    saldoAlLlegar: enteroONull(v.saldoAlLlegar),
    metaAlLlegar: enteroONull(v.metaAlLlegar),
    aportacionesAlLlegar: enteroONull(v.aportacionesAlLlegar),
    abiertaEn: fechaONull(v.abiertaEn),
    respuesta: textoONull(v.respuesta, LARGO_RESPUESTA),
  };
}

/** Une los aportes: llegada y apertura más tempranas; firmas y respuestas, todas. */
export function fusionarAportes(
  base: RecuerdoBase,
  aportes: Readonly<Record<string, AporteDispositivo>>,
): Pick<Recuerdo, 'llegada' | 'abierta' | 'firmas' | 'respuestas'> {
  let llegada: Llegada | null = null;
  let abierta: Recuerdo['abierta'] = null;
  const firmas: LineaFirmada[] = [];
  const respuestas: LineaFirmada[] = [];
  for (const disp of Object.keys(aportes).sort()) {
    const a = aportes[disp];
    if (
      a.llegadaEn &&
      a.saldoAlLlegar != null &&
      a.metaAlLlegar != null &&
      (!llegada || a.llegadaEn < llegada.fecha)
    ) {
      llegada = {
        fecha: a.llegadaEn,
        saldo: a.saldoAlLlegar,
        meta: a.metaAlLlegar,
        aportaciones: a.aportacionesAlLlegar,
        ajustada: a.metaAlLlegar < base.metaAlZarpar,
        diasRespectoPlazo: monthUtils.differenceInCalendarDays(
          a.llegadaEn,
          base.ciclo,
        ),
      };
    }
    if (a.abiertaEn && (!abierta || a.abiertaEn < abierta.fecha)) {
      abierta = { fecha: a.abiertaEn, quien: a.quien };
    }
    if (a.firma) {
      firmas.push({ disp, quien: a.quien, texto: a.firma });
    }
    if (a.respuesta) {
      respuestas.push({ disp, quien: a.quien, texto: a.respuesta });
    }
  }
  return { llegada, abierta, firmas, respuestas };
}

function montar(
  base: RecuerdoBase,
  aportes: Record<string, AporteDispositivo>,
  tieneFoto: boolean,
): Recuerdo {
  return { base, aportes, tieneFoto, ...fusionarAportes(base, aportes) };
}

/**
 * Todos los recuerdos del estado `prefs.synced` (sin índice: por prefijo).
 * Descarta los rid sin base válida y los valores vacíos (lápidas). Orden:
 * llegada (o zarpe) más reciente primero.
 */
export function recuerdosDesdePrefs(
  synced: Readonly<Record<string, string | null | undefined>>,
): Recuerdo[] {
  const bases = new Map<string, RecuerdoBase>();
  const aportes = new Map<string, Record<string, AporteDispositivo>>();
  const fotos = new Set<string>();
  for (const [clave, valor] of Object.entries(synced)) {
    if (!valor) {
      continue;
    }
    const leida = leerClave(clave);
    if (!leida) {
      continue;
    }
    if (leida.parte === 'base') {
      const base = parseBase(valor);
      if (base && base.rid === leida.rid) {
        bases.set(leida.rid, base);
      }
    } else if (leida.parte === 'aporte') {
      const aporte = parseAporte(valor);
      if (aporte) {
        const delRid = aportes.get(leida.rid) ?? {};
        delRid[leida.disp] = aporte;
        aportes.set(leida.rid, delRid);
      }
    } else {
      fotos.add(leida.rid);
    }
  }
  const lista = [...bases.values()].map(base =>
    montar(base, aportes.get(base.rid) ?? {}, fotos.has(base.rid)),
  );
  return lista.sort((a, b) => {
    const fa = a.llegada?.fecha ?? a.base.zarpoEn;
    const fb = b.llegada?.fecha ?? b.base.zarpoEn;
    return fa === fb ? a.base.rid.localeCompare(b.base.rid) : fa < fb ? 1 : -1;
  });
}

// ---------------------------------------------------------------------------
// Vistas útiles para la interfaz
// ---------------------------------------------------------------------------

/** Cumplidas: con llegada (van a la bitácora y a la repisa). */
export function cumplidas(recuerdos: readonly Recuerdo[]): Recuerdo[] {
  return recuerdos.filter(r => r.llegada);
}
/** En curso: navegando y sin llegada (páginas abiertas, botellas en el mar). */
export function enCurso(recuerdos: readonly Recuerdo[]): Recuerdo[] {
  return recuerdos.filter(r => !r.llegada && r.base.estado === 'navegando');
}
/** Cajón «Archivadas»: cerradas sin cumplir y archivadas. */
export function archivadas(recuerdos: readonly Recuerdo[]): Recuerdo[] {
  return recuerdos.filter(r => !r.llegada && r.base.estado === 'archivada');
}

/** Ha llegado y ya se puede abrir («Guardar cerrada hasta…» respetado). */
export function puedeAbrirse(r: Recuerdo, hoy: string): boolean {
  return !!r.llegada && (!r.base.abrirDesde || hoy >= r.base.abrirDesde);
}

/** Ha llegado y este dispositivo aún no la ha abierto (toca celebración). */
export function pendienteDeAbrir(r: Recuerdo, disp: string): boolean {
  return !!r.llegada && !r.aportes[disp]?.abiertaEn;
}

// ---------------------------------------------------------------------------
// Detección de la llegada
// ---------------------------------------------------------------------------

/** Un mes de la hoja (céntimos). El mes actual va con su valor vivo. */
export type Observacion = {
  /** `AAAA-MM`. */
  mes: string;
  /** `leftover-<id>`. */
  saldo: IntegerAmount;
  /** `sum-amount-<id>` (negativo si es gasto). */
  gastado: IntegerAmount;
  /** `budget-<id>`: lo asignado ese mes (para contar aportaciones). */
  asignado?: IntegerAmount;
};

/** Máximo de meses que se miran hacia atrás (como `useDatosObjetivos`). */
export const MESES_SERIE = 24;

/** Meses de la serie de una travesía: desde el zarpe hasta hoy, ≤ MESES_SERIE. */
export function mesesDeSerie(zarpoEn: string, hoy: string): string[] {
  const hasta = hoy.slice(0, 7);
  const desde = zarpoEn.slice(0, 7);
  if (desde > hasta) {
    return [hasta];
  }
  const meses = monthUtils.rangeInclusive(desde, hasta);
  return meses.slice(-MESES_SERIE);
}

/** Ciclo de la travesía que zarpa hoy (una-vez: su fecha). */
export function cicloDe(objetivo: ObjetivoConFecha, hoy: string): string {
  return proximoVencimiento(objetivo, hoy);
}

/** El objetivo es de los que hacen travesía (una vez / cada año). */
export function esObjetivoConFecha(
  objetivo: Objetivo | null | 'otro' | undefined,
): objetivo is ObjetivoConFecha {
  return (
    !!objetivo &&
    objetivo !== 'otro' &&
    (objetivo.tipo === 'una-vez' || objetivo.tipo === 'anual')
  );
}

/**
 * Armada: el saldo estuvo por debajo de la meta después de zarpar, o hubo un
 * gasto (posterior al zarpe) que se la comió. Evita celebrar lo que ya estaba
 * cubierto y que un anual sin gastar «llegue» solo cada ciclo.
 */
export function estaArmada(
  base: Pick<RecuerdoBase, 'saldoAlZarpar' | 'zarpoEn'> &
    Partial<Pick<RecuerdoBase, 'gastadoAlZarpar'>>,
  serie: readonly Observacion[],
  meta: IntegerAmount,
): boolean {
  if (base.saldoAlZarpar < meta) {
    return true;
  }
  const desde = base.zarpoEn.slice(0, 7);
  let gasto = 0;
  for (const o of serie) {
    if (o.mes < desde) {
      continue;
    }
    if (o.saldo < meta) {
      return true;
    }
    // En el mes de zarpe solo cuenta lo gastado después de zarpar.
    const previo = o.mes === desde ? (base.gastadoAlZarpar ?? 0) : 0;
    gasto += Math.max(0, -(o.gastado - previo));
  }
  return gasto >= meta;
}

/** Meses con dinero asignado desde el mes de zarpe (marcas del cuello). */
export function contarAportaciones(
  serie: readonly Observacion[],
  zarpoEn: string,
): number {
  const desde = zarpoEn.slice(0, 7);
  return serie.filter(o => o.mes >= desde && (o.asignado ?? 0) > 0).length;
}

export type EntradaLlegada = {
  objetivo: Objetivo | null | 'otro';
  recuerdo: Recuerdo | null;
  saldo: IntegerAmount;
  serie: readonly Observacion[];
  soloLectura: boolean;
};

export type ResultadoLlegada =
  /** Sin objetivo con fecha, o travesía cerrada: nada que hacer. */
  | { tipo: 'no-aplica' }
  /** Hay objetivo con fecha pero no base: el detector la crea (si puede escribir). */
  | { tipo: 'adoptar' }
  | { tipo: 'navegando'; progreso: number }
  /** Saldo ≥ meta pero ya lo estaba al zarpar y no ha bajado. */
  | { tipo: 'sin-armar' }
  /** Este dispositivo debe marcar la llegada (y celebrar). */
  | { tipo: 'marcar'; meta: IntegerAmount }
  | { tipo: 'llegada'; llegada: Llegada }
  /** Solo lectura: ha llegado pero nadie que navega la ha marcado. */
  | { tipo: 'en-muelle' };

export function evaluarLlegada(e: EntradaLlegada): ResultadoLlegada {
  if (!esObjetivoConFecha(e.objetivo)) {
    return { tipo: 'no-aplica' };
  }
  const meta = metaDeObjetivo(e.objetivo);
  if (meta == null || meta <= 0) {
    return { tipo: 'no-aplica' };
  }
  if (!e.recuerdo) {
    return e.soloLectura ? { tipo: 'no-aplica' } : { tipo: 'adoptar' };
  }
  if (e.recuerdo.llegada) {
    return { tipo: 'llegada', llegada: e.recuerdo.llegada };
  }
  if (e.recuerdo.base.estado !== 'navegando') {
    return { tipo: 'no-aplica' };
  }
  if (e.saldo < meta) {
    return {
      tipo: 'navegando',
      progreso: Math.max(0, Math.min(1, e.saldo / meta)),
    };
  }
  if (!estaArmada(e.recuerdo.base, e.serie, meta)) {
    return { tipo: 'sin-armar' };
  }
  return e.soloLectura ? { tipo: 'en-muelle' } : { tipo: 'marcar', meta };
}

// ---------------------------------------------------------------------------
// Escrituras (puras: devuelven el valor nuevo; el hook lo guarda)
// ---------------------------------------------------------------------------

export function aporteVacio(quien: string | null): AporteDispositivo {
  return {
    v: 1,
    quien,
    firma: null,
    llegadaEn: null,
    saldoAlLlegar: null,
    metaAlLlegar: null,
    aportacionesAlLlegar: null,
    abiertaEn: null,
    respuesta: null,
  };
}

function conQuien(
  previo: AporteDispositivo | null | undefined,
  quien: string | null,
): AporteDispositivo {
  const a = previo ?? aporteVacio(quien);
  return a.quien ? a : { ...a, quien };
}

/** Aporte con la llegada marcada. Idempotente: no pisa una llegada previa. */
export function aporteConLlegada(
  previo: AporteDispositivo | null,
  datos: {
    hoy: string;
    saldo: IntegerAmount;
    meta: IntegerAmount;
    quien: string | null;
    aportaciones?: number | null;
  },
): AporteDispositivo {
  if (previo?.llegadaEn) {
    return previo;
  }
  return {
    ...conQuien(previo, datos.quien),
    llegadaEn: datos.hoy,
    saldoAlLlegar: datos.saldo,
    metaAlLlegar: datos.meta,
    aportacionesAlLlegar: datos.aportaciones ?? null,
  };
}

/** «Firmar también» (o cambiar la firma de este dispositivo; '' la quita). */
export function aporteConFirma(
  previo: AporteDispositivo | null,
  firma: string,
  quien: string | null,
): AporteDispositivo {
  const limpia = firma.trim().slice(0, LARGO_FIRMA);
  return { ...conQuien(previo, quien), firma: limpia || null };
}

/** Abierta en este dispositivo. Idempotente: conserva la primera fecha. */
export function aporteAbierta(
  previo: AporteDispositivo | null,
  hoy: string,
  quien: string | null,
): AporteDispositivo {
  if (previo?.abiertaEn) {
    return previo;
  }
  return { ...conQuien(previo, quien), abiertaEn: hoy };
}

/** «Responder a quien zarpó» ('' la quita). */
export function aporteConRespuesta(
  previo: AporteDispositivo | null,
  respuesta: string,
  quien: string | null,
): AporteDispositivo {
  const limpia = respuesta.trim().slice(0, LARGO_RESPUESTA);
  return { ...conQuien(previo, quien), respuesta: limpia || null };
}

export type DatosZarpe = {
  categoriaId: string;
  objetivo: ObjetivoConFecha;
  nombre: string;
  emoji: string | null;
  /** `leftover-<id>` del mes actual al zarpar. */
  saldo: IntegerAmount;
  /** `sum-amount-<id>` del mes actual al zarpar. */
  gastado?: IntegerAmount;
  hoy: string;
  clase?: IdMeta;
  carta?: string;
  siEntonces?: string;
  autor?: string | null;
  abrirDesde?: string | null;
};

/** Base nueva: la crea la adopción (sin carta) o `zarpar` (con carta). */
export function nuevaBase(datos: DatosZarpe): RecuerdoBase {
  const ciclo = cicloDe(datos.objetivo, datos.hoy);
  return {
    v: 1,
    rid: ridDe(datos.categoriaId, ciclo),
    categoriaId: datos.categoriaId,
    tipo: datos.objetivo.tipo,
    ciclo,
    clase: datos.clase ?? 'otra',
    nombre: datos.nombre,
    emoji: datos.emoji,
    metaAlZarpar: datos.objetivo.importe,
    saldoAlZarpar: datos.saldo,
    gastadoAlZarpar: Math.min(0, datos.gastado ?? 0),
    zarpoEn: datos.hoy,
    carta: (datos.carta ?? '').trim().slice(0, LARGO_CARTA),
    siEntonces: (datos.siEntonces ?? '').trim().slice(0, LARGO_SI_ENTONCES),
    autor: datos.autor ?? null,
    abrirDesde: fechaONull(datos.abrirDesde),
    estado: 'navegando',
    rumboA: null,
    cerradaEn: null,
  };
}

/**
 * Base de la adopción (objetivo con fecha sin base para su ciclo). Si la
 * categoría tiene otra travesía navegando sin llegar (se cambió la fecha, o
 * un anual pasó de ciclo sin llegar), la nueva hereda su carta, y en las de
 * una vez también el zarpe; la vieja se cierra «cambiando de rumbo» hacia la
 * nueva. Es determinista: dos móviles escriben lo mismo.
 */
export function adoptar(
  datos: DatosZarpe,
  previas: readonly Recuerdo[],
): { base: RecuerdoBase; cerradas: RecuerdoBase[] } {
  const nueva = nuevaBase(datos);
  const vivas = previas
    .filter(
      r =>
        r.base.categoriaId === datos.categoriaId &&
        r.base.rid !== nueva.rid &&
        r.base.estado === 'navegando' &&
        !r.llegada,
    )
    .sort((a, b) => (a.base.zarpoEn < b.base.zarpoEn ? -1 : 1));
  const madre = vivas[0]?.base;
  if (!madre) {
    return { base: nueva, cerradas: [] };
  }
  const base: RecuerdoBase = {
    ...nueva,
    clase: datos.clase ?? madre.clase,
    carta: madre.carta,
    siEntonces: madre.siEntonces,
    autor: madre.autor,
    abrirDesde: madre.abrirDesde,
    ...(madre.tipo === 'una-vez' && nueva.tipo === 'una-vez'
      ? {
          zarpoEn: madre.zarpoEn,
          saldoAlZarpar: madre.saldoAlZarpar,
          gastadoAlZarpar: madre.gastadoAlZarpar,
          metaAlZarpar: madre.metaAlZarpar,
        }
      : {}),
  };
  return {
    base,
    cerradas: vivas.map(r =>
      baseCerrada(r.base, { tipo: 'rumbo', destino: base.rid }, datos.hoy),
    ),
  };
}

/** Editar la carta, la línea «si-entonces» o «Guardar cerrada hasta…». */
export function baseConCarta(
  base: RecuerdoBase,
  cambios: {
    carta?: string;
    siEntonces?: string;
    abrirDesde?: string | null;
    autor?: string | null;
  },
): RecuerdoBase {
  return {
    ...base,
    ...(cambios.carta !== undefined && {
      carta: cambios.carta.trim().slice(0, LARGO_CARTA),
    }),
    ...(cambios.siEntonces !== undefined && {
      siEntonces: cambios.siEntonces.trim().slice(0, LARGO_SI_ENTONCES),
    }),
    ...(cambios.abrirDesde !== undefined && {
      abrirDesde: fechaONull(cambios.abrirDesde),
    }),
    ...(cambios.autor !== undefined && { autor: cambios.autor }),
  };
}

/** Las tres salidas del diálogo de cierre de una travesía sin cumplir. */
export type SalidaTravesia =
  /** Devolver la botella al mar: se borra la carta, no queda rastro. */
  | { tipo: 'devolver' }
  /** Archivar: carta y datos al cajón «Archivadas». */
  | { tipo: 'archivar' }
  /** Cambiar de rumbo: la carta pasa a otra travesía (`destino` = su rid). */
  | { tipo: 'rumbo'; destino: string };

/**
 * Base cerrada. Devolver vacía la carta (la base queda como lápida para que
 * el detector no la vuelva a adoptar); cambiar de rumbo también, porque la
 * carta vive ya en la travesía destino (ver `baseConCartaHeredada`).
 */
export function baseCerrada(
  base: RecuerdoBase,
  salida: SalidaTravesia,
  hoy: string,
): RecuerdoBase {
  if (salida.tipo === 'archivar') {
    return { ...base, estado: 'archivada', cerradaEn: hoy };
  }
  return {
    ...base,
    estado: 'devuelta',
    carta: '',
    siEntonces: '',
    autor: null,
    rumboA: salida.tipo === 'rumbo' ? salida.destino : null,
    cerradaEn: hoy,
  };
}

/** La travesía destino de «cambiar de rumbo» hereda la carta si no tenía. */
export function baseConCartaHeredada(
  destino: RecuerdoBase,
  origen: RecuerdoBase,
): RecuerdoBase {
  if (destino.carta) {
    return destino;
  }
  return {
    ...destino,
    carta: origen.carta,
    siEntonces: destino.siEntonces || origen.siEntonces,
    autor: origen.autor,
  };
}

/** Tipo de meta de una categoría: el de la «Primera travesía» (paso 6) o «otra». */
export function claseDesdeTravesia(
  categoriaId: string,
  rawTravesia: string | null | undefined,
): IdMeta {
  const meta = leerTravesia(rawTravesia)?.ids?.meta;
  return meta && meta.id === categoriaId && CLASES.includes(meta.clave)
    ? meta.clave
    : 'otra';
}

/** Valor de pref de una base o un aporte. */
export function serializar(valor: RecuerdoBase | AporteDispositivo): string {
  return JSON.stringify(valor);
}

// ---------------------------------------------------------------------------
// Peso, fotos y huérfanos
// ---------------------------------------------------------------------------

/** Caracteres de todas las prefs `recuerdo:*` (los data URL son ASCII). */
export function pesoRecuerdos(
  synced: Readonly<Record<string, string | null | undefined>>,
): number {
  let total = 0;
  for (const [clave, valor] of Object.entries(synced)) {
    if (clave.startsWith(PREFIJO_RECUERDO) && valor) {
      total += clave.length + valor.length;
    }
  }
  return total;
}

export function nivelPeso(bytes: number): 'ok' | 'aviso' | 'sin-fotos' {
  if (bytes >= LIMITE_PESO) {
    return 'sin-fotos';
  }
  return bytes >= AVISO_PESO ? 'aviso' : 'ok';
}

/** Primer intento de la escalera que cabe en el tope (o null). */
export function elegirFoto(
  intentos: readonly { dataUrl: string }[],
): string | null {
  return (
    intentos.find(
      i =>
        i.dataUrl.startsWith('data:image/jpeg;base64,') &&
        i.dataUrl.length <= TOPE_FOTO,
    )?.dataUrl ?? null
  );
}

/** Bases sin llegada cuya categoría ya no existe (se pueden limpiar). */
export function borradoresHuerfanos(
  recuerdos: readonly Recuerdo[],
  categoriasVivas: ReadonlySet<string>,
): string[] {
  return recuerdos
    .filter(
      r =>
        !r.llegada &&
        r.base.estado === 'navegando' &&
        !categoriasVivas.has(r.base.categoriaId),
    )
    .map(r => r.base.rid);
}

/**
 * Categorías que no pueden tener travesía en pantalla: ocultas, de un grupo
 * oculto o borradas. Sus recuerdos en curso no salen en «Navegando» ni avisan
 * de llegadas; los ya cumplidos se quedan en la bitácora.
 */
export function categoriasOcultas(
  list: readonly {
    id: string;
    hidden?: boolean;
    tombstone?: boolean;
    group?: string;
  }[],
  grouped: readonly { id: string; hidden?: boolean }[],
): Set<string> {
  const gruposOcultos = new Set(grouped.filter(g => g.hidden).map(g => g.id));
  const ocultas = new Set<string>();
  for (const c of list) {
    if (c.hidden || c.tombstone || (c.group && gruposOcultos.has(c.group))) {
      ocultas.add(c.id);
    }
  }
  return ocultas;
}
